(() => {
  'use strict';

  const optionPicker = {
    currentVariantId: window.myCustomSaveVariantId || null,
    option1SelectedValue: null,
    option2SelectedValue: null,
    option3SelectedValue: null,
    variantsData: [],
    inventoryVariantMap: {},
    currentInventory: null,
    currentVariant: null,
    optionConfig: null,
    shopifyOptions: [],
  };

  window.optionPicker = optionPicker;

  const getSelectedValues = () => [
    optionPicker.option1SelectedValue,
    optionPicker.option2SelectedValue,
    optionPicker.option3SelectedValue,
  ];

  const getVariantOptions = (variant) => [variant.option1, variant.option2, variant.option3];

  const getRealOptionIndex = (title, fallbackIndex) => {
    const index = optionPicker.shopifyOptions.indexOf(title);
    return index >= 0 ? index : fallbackIndex;
  };

  const initOptionPickerState = () => {
    optionPicker.currentVariant = window.current_variant || null;
    optionPicker.currentVariantId = optionPicker.currentVariant?.id || window.myCustomSaveVariantId || null;
    optionPicker.option1SelectedValue = optionPicker.currentVariant?.option1 || null;
    optionPicker.option2SelectedValue = optionPicker.currentVariant?.option2 || null;
    optionPicker.option3SelectedValue = optionPicker.currentVariant?.option3 || null;
    optionPicker.variantsData = window.myCustomProdSaveObj?.variants || [];
    optionPicker.inventoryVariantMap = window.myCustomVariantInventory || {};
    optionPicker.currentInventory = optionPicker.inventoryVariantMap[optionPicker.currentVariantId] ?? null;
    optionPicker.optionConfig = window.myProdOptionConfig || null;
    optionPicker.shopifyOptions = window.myCustomProdSaveObj?.options || [];
  };

  const isOptionValuePurchasable = (optionIndex, optionValue) => {
    const candidateValues = getSelectedValues();
    candidateValues[optionIndex] = optionValue;

    return optionPicker.variantsData.some((variant) => {
      const variantValues = getVariantOptions(variant);
      const isMatch = candidateValues.every((value, index) => !value || variantValues[index] === value);
      return isMatch && Boolean(variant.available);
    });
  };

  const updateDisabledOptions = (pickerRoot) => {
    pickerRoot.querySelectorAll('.my-variantselect-option-value').forEach((optionElement) => {
      const optionItem = optionElement.closest('.my-variantselect-optionitem');
      const optionIndex = Number(optionItem?.dataset.optionIndex || 0);
      const optionValue = optionElement.dataset.optionValue;
      const isUnavailable = !isOptionValuePurchasable(optionIndex, optionValue);

      optionElement.classList.toggle('myoption-disabled', isUnavailable);
      optionElement.dataset.unavailable = isUnavailable ? 'true' : 'false';
      // 不可售和不存在的组合仍允许切换；购买按钮会根据完整组合单独禁用。
      optionElement.disabled = false;
    });
  };

  const updateSelectedOptionUI = (pickerRoot) => {
    const selectedValues = getSelectedValues();

    pickerRoot.querySelectorAll('.my-variantselect-option-value').forEach((optionElement) => {
      const optionItem = optionElement.closest('.my-variantselect-optionitem');
      const optionIndex = Number(optionItem?.dataset.optionIndex || 0);
      const isSelected = selectedValues[optionIndex] === optionElement.dataset.optionValue;

      optionElement.classList.toggle('myoption-selected', isSelected);
      optionElement.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  };

  const findVariantForComboItem = (comboOptionIndex, comboItemName) => {
    const selectedValues = getSelectedValues();

    return optionPicker.variantsData.find((variant) => {
      const variantValues = getVariantOptions(variant);
      return variantValues.every((value, index) => {
        if (index === comboOptionIndex) return value === comboItemName;
        return !selectedValues[index] || value === selectedValues[index];
      });
    });
  };

  // UK 折扣结构：productId → variantId → { disCountType, disCountoff, disCountcode, desc }
  const getVariantDiscountConfig = (variantId) => {
    const productId = String(window.myCustomSaveProdId || '');
    const variantIdString = String(variantId || '');
    const rawConfig = window.myProdDCConfigCustomObj?.[productId]?.[variantIdString] || {};

    return {
      disCountType: rawConfig.disCountType || '',
      disCountoff: rawConfig.disCountoff || '',
      disCountcode: rawConfig.disCountcode || '',
      desc: rawConfig.desc || '',
    };
  };

  const formatDiscountLabel = (discountOff) => {
    const label = String(discountOff || '').trim();
    if (!label) return '';

    return `${label.replace(/\s*off\s*$/i, '').trim()}OFF`;
  };

  const formatVariantMoney = (cents) => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    const currencyPrefix = window.myCustomProductMoneyPrefix || '£';
    const moneyFormat = `${currencyPrefix}{{amount}}`;

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount, moneyFormat);
    }
    if (window.vndCurrency?.formatMoney) {
      return window.vndCurrency.formatMoney(amount, moneyFormat);
    }

    const formattedAmount = new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount / 100);
    return `${currencyPrefix}${formattedAmount}`;
  };

  const calculateDiscountedPrice = (priceCents, discountConfig) => {
    const currentPrice = Number(priceCents);
    const discountText = String(discountConfig?.disCountoff || '').trim();
    if (!Number.isFinite(currentPrice) || !discountText) return null;

    // 兼容 "10%"、"£100 OFF"、"100OFF" 等配置格式。
    const discountMatch = discountText.replace(/,/g, '').match(/\d+(?:\.\d+)?/);
    const discountValue = discountMatch ? Number(discountMatch[0]) : NaN;
    if (!Number.isFinite(discountValue) || discountValue <= 0) return null;

    const discountType = String(discountConfig?.disCountType || '').trim().toLowerCase();
    let discountedPrice;
    if (discountType === 'off') {
      discountedPrice = currentPrice * (1 - (discountValue / 100));
    } else if (discountType === 'money') {
      discountedPrice = currentPrice - (discountValue * 100);
    } else {
      return null;
    }

    // 与折扣 banner 一致：折后价向上取整到分，容差仅用于消除浮点运算尾差。
    const roundingTolerance = Number.EPSILON * Math.max(1, currentPrice) * 4;
    return Math.max(0, Math.ceil(discountedPrice - roundingTolerance));
  };

  const findConfiguredOptionElement = (pickerRoot, optionIndex, optionValue) => {
    const optionItem = pickerRoot.querySelector(`.my-variantselect-optionitem[data-option-index="${optionIndex}"]`);
    if (!optionItem) return null;

    return Array.from(optionItem.querySelectorAll('.my-variantselect-option-value')).find(
      (element) => element.dataset.optionValue === optionValue,
    );
  };

  const appendComboContents = (optionElement, contentItems) => {
    const container = optionElement.querySelector('.my-variantselect-option-combovalue-cont');
    if (!container || !contentItems?.length) return;

    const contentWrap = document.createElement('span');
    contentWrap.className = 'my-variantselect-option-combovalue-content';

    const title = document.createElement('span');
    title.className = 'my-variantselect-option-combovalue-content-title';
    title.textContent = 'Contents:';
    contentWrap.appendChild(title);

    const itemsWrap = document.createElement('span');
    itemsWrap.className = 'my-variantselect-option-combovalue-content-items';

    contentItems.forEach((contentItem) => {
      const item = document.createElement('span');
      item.className = 'my-variantselect-option-combovalue-content-item';

      if (Number(contentItem.num) > 1) {
        const quantity = document.createElement('span');
        quantity.className = 'my-variantselect-option-combovalue-content-item-num';
        quantity.textContent = `×${contentItem.num}`;
        item.appendChild(quantity);
      }

      const image = document.createElement('img');
      image.src = contentItem.imgurl || '';
      image.alt = contentItem.name || '';
      image.width = 60;
      image.height = 60;
      image.loading = 'lazy';
      image.decoding = 'async';
      item.appendChild(image);
      itemsWrap.appendChild(item);
    });

    contentWrap.appendChild(itemsWrap);
    container.appendChild(contentWrap);
  };

  const updateAllComboOptionsUI = (pickerRoot) => {
    if (!optionPicker.optionConfig?.options?.length) return;

    const selectedValues = getSelectedValues();

    optionPicker.optionConfig.options.forEach((optionConfigItem, configuredIndex) => {
      if (optionConfigItem.type !== 'combo') return;

      const optionIndex = getRealOptionIndex(optionConfigItem.title, configuredIndex);
      const selectedValue = selectedValues[optionIndex];

      optionConfigItem.values?.forEach((comboItem) => {
        const optionElement = findConfiguredOptionElement(pickerRoot, optionIndex, comboItem.name);
        if (!optionElement) return;

        const matchingVariant = findVariantForComboItem(optionIndex, comboItem.name);
        const variantId = matchingVariant?.id?.toString();
        const variantConfig = variantId ? comboItem.variantConfig?.[variantId] : null;

        const image = optionElement.querySelector('.my-variantselect-option-combovalue-image img');
        if (image) {
          image.hidden = !variantConfig?.imgUrl;
          if (variantConfig?.imgUrl) image.src = variantConfig.imgUrl;
        }

        const price = Number(matchingVariant?.price) || 0;
        const compareAtPrice = Number(matchingVariant?.compare_at_price) || 0;
        const discountConfig = getVariantDiscountConfig(variantId);
        const discountLabel = formatDiscountLabel(discountConfig.disCountoff);
        const discountedPrice = calculateDiscountedPrice(price, discountConfig);
        const hasDiscount = discountedPrice !== null;
        const displayedPrice = hasDiscount ? discountedPrice : price;
        const crossedPrice = hasDiscount ? price : compareAtPrice;
        const showCrossedPrice = hasDiscount
          ? price > discountedPrice
          : compareAtPrice > price;

        const priceElement = optionElement.querySelector('.my-variantselect-option-combovalue-infoprice-price');
        if (priceElement) {
          priceElement.innerHTML = matchingVariant ? formatVariantMoney(displayedPrice) : '';
        }

        const description = optionElement.querySelector('.my-variantselect-option-combovalue-infoprice-desc');
        if (description) {
          description.hidden = !hasDiscount;
          description.textContent = hasDiscount ? 'Now' : '';
        }

        const compareAtPriceElement = optionElement.querySelector('.my-variantselect-option-combovalue-infoprice-comprice');
        if (compareAtPriceElement) {
          compareAtPriceElement.hidden = !showCrossedPrice;
          compareAtPriceElement.innerHTML = showCrossedPrice
            ? `<span class="my-variantselect-option-combovalue-infoprice-comprice-value">${formatVariantMoney(crossedPrice)}</span>`
            : '';
        }

        const discountElement = optionElement.querySelector('.my-variantselect-option-combovalue-dc');
        if (discountElement) {
          discountElement.textContent = discountLabel;
          discountElement.title = discountConfig.desc;
        }
        optionElement.dataset.discountType = discountConfig.disCountType;
        optionElement.dataset.discountOff = discountConfig.disCountoff;
        optionElement.dataset.discountCode = discountConfig.disCountcode;
        optionElement.dataset.discountDesc = discountConfig.desc;

        /*
         * 暂时隐藏 combo 内容物展示，保留动态更新逻辑便于后续恢复。
         * optionElement.querySelector('.my-variantselect-option-combovalue-content')?.remove();
         * if (comboItem.name === selectedValue) {
         *   appendComboContents(optionElement, variantConfig?.content);
         * }
         */
      });
    });
  };

  const getSelectionState = (selectedVariant) => {
    if (!selectedVariant) return 'unavailable';
    return selectedVariant.available ? 'available' : 'soldout';
  };

  const setButtonDisabledByVariant = (button, shouldDisable) => {
    if (!button) return;

    if (shouldDisable) {
      if (!button.disabled) button.dataset.variantPickerCanRestore = 'true';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    if (button.dataset.variantPickerCanRestore === 'true') {
      button.disabled = false;
    }
    delete button.dataset.variantPickerCanRestore;
    button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
  };

  const syncPurchaseAvailability = (selectedVariant) => {
    const formWrapper = document.querySelector('.my-custom-new-variant-form');
    const productForm = formWrapper?.querySelector('form.product-form');
    if (!productForm) return;

    const selectionState = getSelectionState(selectedVariant);
    const isPurchasable = selectionState === 'available';
    const productContainer = productForm.closest('.products-template')
      || document.querySelector('.products-template');
    const addButton = productForm.querySelector('#shopifyOriginAddToCartFormId button[name="add"]')
      || productForm.querySelector('button[name="add"]');
    const buttonText = addButton?.querySelector('.add-cart__text');

    productForm.dataset.variantSelectionState = selectionState;
    productContainer?.classList.toggle('product-disabled', !isPurchasable);
    setButtonDisabledByVariant(addButton, !isPurchasable);

    const unavailableText = selectionState === 'soldout' ? 'Sold Out' : 'Unavailable';
    if (!isPurchasable && buttonText && buttonText.textContent !== unavailableText) {
      buttonText.textContent = unavailableText;
    }

    productForm.querySelectorAll(
      '.shopify-payment-button button, .shopify-payment-button__button',
    ).forEach((button) => setButtonDisabledByVariant(button, !isPurchasable));
  };

  const syncLegacyProductForm = (selectedVariant) => {
    const formWrapper = document.querySelector('.my-custom-new-variant-form');
    const productForm = formWrapper?.querySelector('form.product-form');
    if (!productForm) return;

    const selectedOptions = getSelectedValues();
    const generatedSelectors = productForm.querySelectorAll('.single-option-selector');

    generatedSelectors.forEach((selector, index) => {
      selector.value = selectedOptions[index];
    });

    if (generatedSelectors.length) {
      generatedSelectors[0].dispatchEvent(new Event('change', { bubbles: true }));
    }

    const actualVariantSelect = productForm.querySelector('#product-actual-select');
    if (actualVariantSelect) {
      actualVariantSelect.value = selectedVariant?.available ? selectedVariant.id : '';
      actualVariantSelect.disabled = !selectedVariant?.available;
      actualVariantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    productForm.querySelectorAll('.product-single-option').forEach((optionGroup) => {
      const optionIndex = Number(optionGroup.dataset.optionIndex || 1) - 1;
      optionGroup.querySelectorAll('.option-label').forEach((label) => {
        label.classList.toggle('active', label.dataset.value === selectedOptions[optionIndex]);
      });
    });

    syncPurchaseAvailability(selectedVariant);
    if (selectedVariant && typeof window.changeTitle === 'function') window.changeTitle();
  };

  const initPurchaseGuards = () => {
    const formWrapper = document.querySelector('.my-custom-new-variant-form');
    const productForm = formWrapper?.querySelector('form.product-form');
    if (!productForm) return;

    productForm.addEventListener('submit', (event) => {
      if (productForm.dataset.variantSelectionState === 'available') return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    productForm.addEventListener('click', (event) => {
      if (productForm.dataset.variantSelectionState === 'available') return;
      if (!event.target.closest?.('button[name="add"], .shopify-payment-button')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    if ('MutationObserver' in window) {
      const paymentButtonObserver = new MutationObserver(() => {
        syncPurchaseAvailability(optionPicker.currentVariant);
      });
      paymentButtonObserver.observe(productForm, { childList: true, subtree: true });
    }
  };

  const selectVariant = (pickerRoot, optionIndex, optionValue) => {
    const nextSelectedValues = getSelectedValues();
    if (nextSelectedValues[optionIndex] === optionValue) return;

    nextSelectedValues[optionIndex] = optionValue;

    const selectedVariant = optionPicker.variantsData.find((variant) => {
      const variantValues = getVariantOptions(variant);
      return nextSelectedValues.every((value, index) => !value || variantValues[index] === value);
    });

    optionPicker.option1SelectedValue = nextSelectedValues[0];
    optionPicker.option2SelectedValue = nextSelectedValues[1];
    optionPicker.option3SelectedValue = nextSelectedValues[2];
    optionPicker.currentVariantId = selectedVariant?.id || null;
    optionPicker.currentInventory = selectedVariant
      ? optionPicker.inventoryVariantMap[selectedVariant.id] ?? null
      : null;
    optionPicker.currentVariant = selectedVariant || null;

    const url = new URL(window.location.href);
    if (selectedVariant) {
      url.searchParams.set('variant', selectedVariant.id);
    } else {
      url.searchParams.delete('variant');
    }
    window.history.replaceState({}, '', url.toString());

    updateSelectedOptionUI(pickerRoot);
    updateAllComboOptionsUI(pickerRoot);
    updateDisabledOptions(pickerRoot);
    syncLegacyProductForm(selectedVariant);

    document.dispatchEvent(new CustomEvent('myOptionChange', {
      detail: {
        option1: optionPicker.option1SelectedValue,
        option2: optionPicker.option2SelectedValue,
        option3: optionPicker.option3SelectedValue,
        variantId: optionPicker.currentVariantId,
        inventory: optionPicker.currentInventory,
        selectionState: getSelectionState(selectedVariant),
        available: Boolean(selectedVariant?.available),
        selectedOptions: getSelectedValues(),
        discount: getVariantDiscountConfig(optionPicker.currentVariantId),
      },
    }));
  };

  const initOptionClicks = (pickerRoot) => {
    pickerRoot.querySelectorAll('.my-variantselect-option-value').forEach((optionElement) => {
      optionElement.addEventListener('click', () => {
        const optionItem = optionElement.closest('.my-variantselect-optionitem');
        const optionIndex = Number(optionItem?.dataset.optionIndex || 0);
        selectVariant(pickerRoot, optionIndex, optionElement.dataset.optionValue);
      });
    });
  };

  const initProductSeriesSlider = () => {
    document.querySelectorAll('.my-custom-special-product-ui .my-product-series-contentwrap').forEach((wrapper) => {
      const track = wrapper.querySelector('.my-product-series-itemwrap');
      const previousButton = wrapper.querySelector('.my-product-series-changebtn.prev');
      const nextButton = wrapper.querySelector('.my-product-series-changebtn.next');
      const firstItem = track?.querySelector('.my-product-series-item');
      if (!track || !previousButton || !nextButton || !firstItem) return;

      const getStep = () => {
        const styles = window.getComputedStyle(track);
        const gap = Number.parseFloat(styles.columnGap) || Number.parseFloat(styles.gap) || 0;
        return firstItem.getBoundingClientRect().width + gap;
      };
      const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
      const updateButtons = () => {
        const maxScroll = getMaxScroll();
        const hasOverflow = maxScroll > 1;
        previousButton.hidden = !hasOverflow;
        nextButton.hidden = !hasOverflow;
        previousButton.classList.toggle('is-disabled', track.scrollLeft <= 0);
        nextButton.classList.toggle('is-disabled', track.scrollLeft >= maxScroll - 1);
      };

      previousButton.addEventListener('click', () => {
        if (!previousButton.classList.contains('is-disabled')) {
          track.scrollBy({ left: -getStep(), behavior: 'smooth' });
        }
      });
      nextButton.addEventListener('click', () => {
        if (!nextButton.classList.contains('is-disabled')) {
          track.scrollBy({ left: getStep(), behavior: 'smooth' });
        }
      });
      track.addEventListener('scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons);
      updateButtons();
    });
  };

  const init = () => {
    initProductSeriesSlider();

    const pickerRoot = document.querySelector('.my-custom-product-variant-picker');
    if (!pickerRoot) return;

    initOptionPickerState();
    updateDisabledOptions(pickerRoot);
    updateAllComboOptionsUI(pickerRoot);
    updateSelectedOptionUI(pickerRoot);
    initPurchaseGuards();
    syncPurchaseAvailability(optionPicker.currentVariant);
    initOptionClicks(pickerRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
